import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ClubHistory = () => {
  const decades = [
    {
      decade: '1998-2000',
      title: 'The Early Years',
      description: 'NLNG RA Club began its journey in 1998, establishing a foundation of community, recreation, and excellence for NLNG employees and Finima residents.',
      image: 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?auto=format&fit=crop&w=800&q=80',
      highlights: [
        '1998: Club founded with charter members',
        '1999: First tennis courts constructed',
        '1999: Membership grows steadily',
        '2000: Initial clubhouse facilities completed'
      ]
    },
    {
      decade: '2000s',
      title: 'Expansion Era',
      description: 'The club underwent major renovations and expansions, adding new facilities and attracting more members.',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&q=80',
      highlights: [
        '2000: Swimming pool completed',
        '2003: Clubhouse expansion',
        '2005: Golf course added',
        '2008: Wellness center opened'
      ]
    },
    {
      decade: '2010s',
      title: 'Recognition & Growth',
      description: 'The club gained national recognition, won multiple awards, and became the premier residential club in Nigeria.',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80',
      highlights: [
        '2010: Membership reaches 1,000',
        '2013: Awarded "Best Club in Nigeria"',
        '2015: Major facility upgrade',
        '2018: International affiliations'
      ]
    },
    {
      decade: '2020s',
      title: 'Digital Transformation',
      description: 'Embracing technology, the club launched digital services, online booking, and virtual experiences for members.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80',
      highlights: [
        '2020: Digital services launched',
        '2022: Mobile app released',
        '2023: 2,500+ members',
        '2024: Smart facility management'
      ]
    }
  ];

  const [activeDecade, setActiveDecade] = React.useState(0);

  return (
    <div className="history-page">
      {/* Hero Section */}
      <section className="page-hero history-hero">
        <div className="hero-overlay"></div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="hero-eyebrow">THE CLUB</span>
            <h1 className="hero-title">Club <span className="text-green">History</span></h1>
            <p className="hero-subtitle">Tracing the remarkable journey of NLNG RA Club from 1998 to present</p>
          </motion.div>
        </div>
      </section>

      {/* Interactive Timeline */}
      <section className="interactive-timeline-section">
        <div className="container">
          <div className="section-header">
            <span className="section-eyebrow">THROUGH THE DECADES</span>
            <h2 className="section-title">Our <span className="text-green">Timeline</span></h2>
            <p className="section-subtitle">Explore the key moments that shaped our club</p>
          </div>

          <div className="decade-selector">
            {decades.map((decade, index) => (
              <button 
                key={index}
                className={`decade-btn ${activeDecade === index ? 'active' : ''}`}
                onClick={() => setActiveDecade(index)}
              >
                {decade.decade}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div 
              key={activeDecade}
              className="decade-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <div className="decade-grid">
                <div className="decade-image">
                  <img src={decades[activeDecade].image} alt={decades[activeDecade].title} />
                </div>
                <div className="decade-info">
                  <h3>{decades[activeDecade].title}</h3>
                  <p className="decade-description">{decades[activeDecade].description}</p>
                  <div className="decade-highlights">
                    <h4>Key Highlights:</h4>
                    <ul>
                      {decades[activeDecade].highlights.map((item, i) => (
                        <li key={i}><i className="fas fa-calendar-check"></i> {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Founding Members */}
      <section className="founding-members-section">
        <div className="container">
          <div className="section-header">
            <span className="section-eyebrow">OUR FOUNDERS</span>
            <h2 className="section-title">The Visionaries <span className="text-green">Behind It All</span></h2>
            <p className="section-subtitle">Honoring the individuals who established our beloved club in 1998</p>
          </div>

          <div className="founding-quote">
            <i className="fas fa-quote-left"></i>
            <p>"We envisioned a place where community could thrive, where families could grow, and where excellence would be the standard. Today, that vision lives on in every corner of NLNG RA Club."</p>
            <span>- Founding Members, 1998</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClubHistory;